import { STAGES } from '../../api/src/programs';
import type { IListSchema } from '../lists/ListStore';

/** Checklist item statuses, in the order a file usually goes through them. */
export const ITEM_STATUSES: string[] = ['Needed', 'Requested', 'Uploaded by client (check)', 'Received', 'Not applicable', 'Waived'];

/**
 * The firm's records, as lists in its own SharePoint site. Addresses (urlName) are stable and
 * product-neutral; titles are what people see in SharePoint.
 */
export const CLIENTS: IListSchema = {
  urlName: 'CaseClients',
  title: 'Clients',
  titleLabel: 'Client',
  columns: [
    { name: 'OtherNames', label: 'Other names', kind: 'text' },
    { name: 'BirthDate', label: 'Date of birth', kind: 'date' },
    { name: 'Email', label: 'Email', kind: 'text' },
    { name: 'Phone', label: 'Phone', kind: 'text' },
    { name: 'Language', label: 'Language', kind: 'text' },
    { name: 'UCI', label: 'UCI', kind: 'text' },
    { name: 'ClientNumber', label: 'Client number', kind: 'text', indexed: true },
    { name: 'ConflictResult', label: 'Conflict check', kind: 'note' },
    { name: 'ConflictBy', label: 'Conflict check by', kind: 'text' },
    { name: 'ConflictOn', label: 'Conflict check on', kind: 'dateTime' },
    { name: 'IdMethod', label: 'ID verified how', kind: 'text' },
    { name: 'IdBy', label: 'ID verified by', kind: 'text' },
    { name: 'IdOn', label: 'ID verified on', kind: 'date' },
    { name: 'Notes', label: 'Notes', kind: 'note' }
  ]
};

export const APPLICATIONS: IListSchema = {
  urlName: 'CaseApplications',
  title: 'Applications',
  titleLabel: 'Application',
  columns: [
    { name: 'ClientKey', label: 'Client', kind: 'text', indexed: true },
    { name: 'Program', label: 'Program', kind: 'text', indexed: true },
    { name: 'ProgramVersion', label: 'Checklist version', kind: 'text' },
    { name: 'Answers', label: 'Answers', kind: 'note', hidden: true },
    { name: 'Stage', label: 'Stage', kind: 'choice', choices: STAGES.map(s => s.label), indexed: true },
    { name: 'Open', label: 'Open', kind: 'boolean', indexed: true },
    { name: 'Lawyer', label: 'Lawyer', kind: 'text' },
    { name: 'Clerk', label: 'Clerk', kind: 'text' },
    { name: 'Opened', label: 'Opened', kind: 'date' },
    { name: 'RetainerSigned', label: 'Retainer signed', kind: 'date' },
    { name: 'RepresentativeSigned', label: 'Use of a Representative signed', kind: 'date' },
    { name: 'Restricted', label: 'Restricted', kind: 'boolean' },
    { name: 'Outstanding', label: 'Items outstanding', kind: 'number' },
    { name: 'NextDue', label: 'Next deadline', kind: 'date', indexed: true },
    { name: 'SubmittedOn', label: 'Submitted on', kind: 'date' },
    { name: 'SubmittedVia', label: 'Submitted through', kind: 'text' },
    { name: 'Confirmation', label: 'Confirmation', kind: 'text' },
    { name: 'Outcome', label: 'Outcome', kind: 'text' },
    { name: 'DecisionDate', label: 'Decision date', kind: 'date' },
    { name: 'ClosedOn', label: 'Closed on', kind: 'date' },
    { name: 'PermitExpires', label: 'Permit expires on', kind: 'date' },
    { name: 'FolderId', label: 'Folder', kind: 'text', hidden: true },
    { name: 'FolderUrl', label: 'Folder link', kind: 'text' }
  ]
};

export const PEOPLE: IListSchema = {
  urlName: 'CasePeople',
  title: 'Application people',
  titleLabel: 'Name',
  columns: [
    { name: 'ApplicationKey', label: 'Application', kind: 'text', indexed: true },
    { name: 'ClientKey', label: 'Client', kind: 'text', indexed: true },
    { name: 'Role', label: 'Role', kind: 'text' },
    { name: 'BirthDate', label: 'Date of birth', kind: 'date' },
    { name: 'Relationship', label: 'Relationship', kind: 'text' }
  ]
};

export const CHECKLIST: IListSchema = {
  urlName: 'CaseChecklist',
  title: 'Application checklists',
  titleLabel: 'Item',
  columns: [
    { name: 'ApplicationKey', label: 'Application', kind: 'text', indexed: true },
    { name: 'PersonKey', label: 'Person', kind: 'text' },
    { name: 'ItemId', label: 'Item id', kind: 'text', hidden: true },
    { name: 'Section', label: 'Section', kind: 'text' },
    { name: 'Form', label: 'Form', kind: 'text' },
    { name: 'Status', label: 'Status', kind: 'choice', choices: ITEM_STATUSES, indexed: true },
    { name: 'ChangedBy', label: 'Changed by', kind: 'text' },
    { name: 'ChangedOn', label: 'Changed on', kind: 'dateTime' },
    { name: 'Documents', label: 'Documents', kind: 'note' },
    { name: 'Note', label: 'Note', kind: 'note' }
  ]
};

export const EVENTS: IListSchema = {
  urlName: 'CaseEvents',
  title: 'Application events and deadlines',
  titleLabel: 'Event',
  columns: [
    { name: 'ApplicationKey', label: 'Application', kind: 'text', indexed: true },
    { name: 'Kind', label: 'Kind', kind: 'text' },
    { name: 'EventKey', label: 'Follows from', kind: 'text', hidden: true },
    { name: 'EventDate', label: 'Date', kind: 'date' },
    { name: 'ReceivedDate', label: 'Date received', kind: 'date' },
    { name: 'Due', label: 'Due', kind: 'date', indexed: true },
    { name: 'RuleId', label: 'Rule', kind: 'text', hidden: true },
    { name: 'RuleSource', label: 'Based on', kind: 'note' },
    { name: 'ConfirmedBy', label: 'Confirmed by', kind: 'text' },
    { name: 'ConfirmedOn', label: 'Confirmed on', kind: 'dateTime' },
    { name: 'DoneBy', label: 'Done by', kind: 'text' },
    { name: 'DoneOn', label: 'Done on', kind: 'dateTime' },
    { name: 'CalendarEventId', label: 'Calendar event', kind: 'text', hidden: true },
    { name: 'Note', label: 'Note', kind: 'note' }
  ]
};

export const ALL_LISTS: IListSchema[] = [CLIENTS, APPLICATIONS, PEOPLE, CHECKLIST, EVENTS];

/** Restricted files' own lists: the same columns, separate lists with their own permissions. */
export function restrictedSchema(s: IListSchema): IListSchema {
  return { ...s, urlName: `${s.urlName}Restricted`, title: `Restricted: ${s.title.charAt(0).toLowerCase()}${s.title.slice(1)}` };
}

export const RESTRICTED_LISTS: IListSchema[] = [CLIENTS, APPLICATIONS, PEOPLE, CHECKLIST, EVENTS].map(restrictedSchema);
export const RESTRICTED_HISTORY: { urlName: string; title: string } = { urlName: 'CaseHistoryRestricted', title: 'Restricted: history' };
export const RESTRICTED_LIBRARY: string = 'Restricted files';
export const RESTRICTED_GROUP: string = 'Restricted files';
